import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import {
  combineYearsMonths,
  MAX_EXPERIENCE_YEARS,
  splitMonthsExperience,
} from './monthsExperience';

type Props = {
  value: number | null;
  isDisabled?: boolean;
  onChange: (totalMonths: number | null) => void;
};

export const MonthsExperienceInput: React.FC<Props> = ({
  value,
  isDisabled,
  onChange,
}) => {
  const t = useNamespacedTranslation('skills.experience');
  const initial = splitMonthsExperience(value);
  const [years, setYears] = useState<string>(
    initial.years === null ? '' : String(initial.years),
  );
  const [months, setMonths] = useState<string>(
    initial.months === null ? '' : String(initial.months),
  );

  // Tracks the last total we either started with or emitted. We only re-sync
  // local state from the `value` prop when it represents a CHANGE that didn't
  // originate from this component — otherwise the optimistic-response loop
  // would clobber digits the user is still typing (e.g. typing "12" months
  // gets clamped to 11 on commit and the effect would overwrite "12" with "11").
  const lastSyncedValueRef = useRef<number | null>(value);

  useEffect(() => {
    if (value === lastSyncedValueRef.current) return;
    lastSyncedValueRef.current = value;
    const next = splitMonthsExperience(value);
    setYears(next.years === null ? '' : String(next.years));
    setMonths(next.months === null ? '' : String(next.months));
  }, [value]);

  const commit = useCallback(
    (yearsStr: string, monthsStr: string) => {
      const y = yearsStr === '' ? null : parseInt(yearsStr, 10);
      const m = monthsStr === '' ? null : parseInt(monthsStr, 10);
      const total = combineYearsMonths(
        Number.isNaN(y as number) ? null : y,
        Number.isNaN(m as number) ? null : m,
      );
      const canonical = splitMonthsExperience(total);
      const canonicalYears =
        canonical.years === null ? '' : String(canonical.years);
      const canonicalMonths =
        canonical.months === null ? '' : String(canonical.months);
      if (canonicalYears !== yearsStr) setYears(canonicalYears);
      if (canonicalMonths !== monthsStr) setMonths(canonicalMonths);
      if (total !== value) {
        lastSyncedValueRef.current = total;
        onChange(total);
      }
    },
    [onChange, value],
  );

  const handleYearsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setYears(next);
      commit(next, months);
    },
    [commit, months],
  );

  const handleMonthsChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const next = e.target.value;
      setMonths(next);
      commit(years, next);
    },
    [commit, years],
  );

  return (
    <div className="d-flex align-items-center gap-1">
      <input
        type="number"
        className="form-control form-control-sm"
        style={{ width: '4rem' }}
        min={0}
        max={MAX_EXPERIENCE_YEARS}
        value={years}
        disabled={isDisabled}
        aria-label={t('add.years')}
        onChange={handleYearsChange}
      />
      <span className="small text-muted">{t('yearsAbbr')}</span>
      <input
        type="number"
        className="form-control form-control-sm"
        style={{ width: '4rem' }}
        min={0}
        max={11}
        value={months}
        disabled={isDisabled}
        aria-label={t('add.months')}
        onChange={handleMonthsChange}
      />
      <span className="small text-muted">{t('monthsAbbr')}</span>
    </div>
  );
};
